import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";

interface DataPoint {
  name: string;
  value: number;
  description: string;
  icon: string;
}

interface SlideContent {
  title?: string;
  subtitle?: string;
  data?: string | DataPoint[];
  teacherNote?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const defaultData: DataPoint[] = [];

const COLORS = ["#f87171", "#60a5fa", "#fbbf24", "#4ade80", "#a78bfa"];

interface EditableDivProps {
  text: string;
  onEdit: (value: string) => void;
  className?: string;
}

const EditableDiv: React.FC<EditableDivProps> = ({
  text,
  onEdit,
  className = "",
}) => {
  return (
    <div
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onEdit(e.currentTarget.textContent || "")}
      className={`outline-none ${className}`}
    >
      {text}
    </div>
  );
};

const DataSlide3 = {
  id: "data-slide3",
  name: "Data Visualization (Pie Chart)",
  fields: [
    { id: "title", label: "Title", type: "text" },
    { id: "subtitle", label: "Subtitle", type: "text" },
    { id: "data", label: "Data", type: "text" },
    { id: "teacherNote", label: "Teacher Note", type: "text" },
    { id: "bgColor", label: "Background Color", type: "text" },
  ],
  render: (content: SlideContent = {}) => {
    const {
      title = "",
      subtitle = "",
      teacherNote = "",
      bgColor = "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)",
      data = "[]",
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);
    };

    let parsedData: DataPoint[] = [];
    try {
      if (typeof data === "string") {
        parsedData = JSON.parse(data);
      } else if (Array.isArray(data)) {
        parsedData = data;
      }
    } catch (error) {
      console.warn("Error parsing data:", error);
    }

    const CustomTooltip: React.FC<{
      active?: boolean;
      payload?: Array<{
        payload: DataPoint;
      }>;
    }> = ({ active, payload }) => {
      if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
          <div className="bg-white/90 p-3 rounded-lg shadow-lg">
            <div className="flex items-center gap-2">
              <span className="text-xl">{data.icon}</span>
              <span className="font-semibold">{data.name}</span>
            </div>
            <p className="text-sm mt-1">{data.description}</p>
            <div className="font-bold mt-1">{data.value}%</div>
          </div>
        );
      }
      return null;
    };

    const handleDataEdit = (
      index: number,
      field: keyof DataPoint,
      value: string,
    ) => {
      const updatedData = [...parsedData];
      updatedData[index] = {
        ...updatedData[index],
        [field]: field === "value" ? Number(value.replace("%", "")) : value,
      };

      handleEdit("data")(JSON.stringify(updatedData));
    };

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
              backgroundSize: "30px 30px",
            }}
          />
        </div>

        {/* Content Container */}
        <div className="relative z-10 flex flex-col h-full p-6">
          {/* Header */}
          <div className="text-center mb-4">
            <EditableDiv
              text={title}
              onEdit={handleEdit("title")}
              className="text-3xl font-bold mb-1 bg-clip-text text-transparent bg-gradient-to-r from-indigo-300 to-purple-300"
            />
            <EditableDiv
              text={subtitle}
              onEdit={handleEdit("subtitle")}
              className="text-xl text-white/80"
            />
          </div>

          {/* Main Content */}
          <div className="flex-1 flex">
            {/* Chart */}
            <div className="w-1/2 h-full bg-white/5 rounded-xl p-4 backdrop-blur-sm">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={parsedData}
                    cx="50%"
                    cy="50%"
                    innerRadius="60%"
                    outerRadius="80%"
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {parsedData.map((entry: DataPoint, index: number) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Description */}
            <div className="w-1/2 pl-4 flex flex-col gap-4">
              {parsedData.map((item: DataPoint, index: number) => (
                <div
                  key={index}
                  className="bg-white/10 rounded-lg p-3 backdrop-blur-sm"
                  style={{
                    borderLeft: `4px solid ${COLORS[index % COLORS.length]}`,
                  }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{item.icon}</span>
                    <EditableDiv
                      text={item.name}
                      onEdit={(value) => handleDataEdit(index, "name", value)}
                      className="font-semibold"
                    />
                    <EditableDiv
                      text={`${item.value}%`}
                      onEdit={(value) => handleDataEdit(index, "value", value)}
                      className="ml-auto font-bold"
                    />
                  </div>
                  <EditableDiv
                    text={item.description}
                    onEdit={(value) =>
                      handleDataEdit(index, "description", value)
                    }
                    className="text-sm text-white/80"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Teacher Note */}
          <div className="mt-4 text-center">
            <EditableDiv
              text={teacherNote}
              onEdit={handleEdit("teacherNote")}
              className="text-sm text-white/70 italic"
            />
          </div>
        </div>
      </div>
    );
  },
};

export default DataSlide3;
